import React, { useEffect, useRef, useState } from 'react'
import { ActivityIndicator, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import { Button, Icon, NotifBadge, Text, TextInput } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { ADMIN_EVENTS_PAGE_SIZE, useAdminEvents, useScreen } from '@hooks'
import {
  activePreset,
  ADMIN_EVENT_CATEGORIES,
  ADMIN_EVENT_FORMATS,
  ADMIN_EVENT_PRICINGS,
  ADMIN_EVENT_SORTS,
  ADMIN_EVENT_STATUSES,
  type AdminEventPricing,
  type AdminEventsFilters,
  type AdminEventSort,
  type AdminEventStatus,
  countActiveAdminEventsFilters,
  EMPTY_ADMIN_EVENTS_FILTERS,
  formatCount,
  hasAnyAdminEventsFilter,
  isInvalidPeriod,
  pageRange,
  parseAdminEventsParams,
  periodPreset,
  type PeriodPreset,
  toAdminEventsParams,
} from '@utils'
import type { AdminStackParamList } from '../../../navigation/navigator/AdminStackNavigator'
import { DateTimeField } from '../events/components/DateTimeField'
import { OptionChips } from '../events/components/OptionChips'
import { AdminEventRow } from './components/AdminEventRow'
import { AdminPage } from './components/AdminPage'
import { OrganizerPicker } from './components/OrganizerPicker'

type Props = NativeStackScreenProps<AdminStackParamList, 'AdminEvents'>

const PRESETS: PeriodPreset[] = ['today', 'week', 'month', 'last30']

/**
 * Every event of the platform. Every filter lives in the page URL (shareable
 * link; coming back from an event finds the same filters). On a phone the
 * filters fold into a "Filters" panel.
 */
export const AdminEvents: React.FC<Props> = ({ navigation, route }) => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const { isMobile } = useScreen()
  const styles = getStyles(theme)
  const filters = parseAdminEventsParams(route.params)
  const [panelOpen, setPanelOpen] = useState(false)
  const showPanel = !isMobile || panelOpen

  // The search box keeps its own text while typing; the URL follows after a short pause.
  const [searchText, setSearchText] = useState(filters.search)
  useEffect(() => setSearchText(filters.search), [filters.search])
  const typing = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(typing.current), [])

  /**
   * A change of filters rewrites the URL of this page (same screen, so an open panel stays open);
   * opening an event and coming back finds the same filters. Back to page 1.
   */
  const commit = (patch: Partial<AdminEventsFilters>) => {
    // What is typed counts even if the URL has not caught up yet (short pause before it does).
    clearTimeout(typing.current)
    navigation.setParams(toAdminEventsParams({ ...filters, search: searchText, page: 1, ...patch }))
  }

  const onSearch = (value: string) => {
    setSearchText(value)
    clearTimeout(typing.current)
    typing.current = setTimeout(
      () => navigation.setParams(toAdminEventsParams({ ...filters, search: value, page: 1 })),
      350,
    )
  }

  const [draftFrom, setDraftFrom] = useState(filters.from)
  const [draftTo, setDraftTo] = useState(filters.to)
  useEffect(() => setDraftFrom(filters.from), [filters.from])
  useEffect(() => setDraftTo(filters.to), [filters.to])
  const periodError = isInvalidPeriod(draftFrom, draftTo)

  const setPeriod = (from: string, to: string) => {
    setDraftFrom(from)
    setDraftTo(to)
    if (!isInvalidPeriod(from, to)) commit({ from, to })
  }

  const { data, isLoading, isFetching, isError } = useAdminEvents(filters)
  const events = data?.data ?? []
  const total = data?.metadata.count ?? 0
  const { from, to } = pageRange(filters.page, ADMIN_EVENTS_PAGE_SIZE, total)
  const lastPage = Math.max(1, Math.ceil(total / ADMIN_EVENTS_PAGE_SIZE))
  const activeCount = countActiveAdminEventsFilters(filters)
  const preset = activePreset(filters.from, filters.to)
  const goToPage = (page: number) => {
    clearTimeout(typing.current)
    navigation.setParams(toAdminEventsParams({ ...filters, search: searchText, page }))
  }

  return (
    <AdminPage
      title={t('admin.events.title')}
      onBack={() => (navigation.getParent() as any)?.navigate('MenuMain')}
      active="events"
    >
      <View style={styles.searchRow}>
        <View style={styles.searchBox}>
          <TextInput
            testID="admin-events-search"
            iconLeft="search"
            placeholder={t('admin.events.searchPlaceholder')}
            value={searchText}
            onChangeText={onSearch}
            autoCapitalize="none"
          />
        </View>
        {isMobile ? (
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={t('filters.title')}
            testID="admin-events-filters-toggle"
            style={styles.filterButton}
            onPress={() => setPanelOpen((current) => !current)}
          >
            <Icon name="filter2" size={16} color={theme.colors.white} />
            <NotifBadge total={activeCount} testID="admin-events-filters-badge" />
          </TouchableOpacity>
        ) : null}
      </View>

      {showPanel ? (
        <View style={styles.panel} testID="admin-events-filters">
          <Text type="subtitleBold">{t('admin.events.period')}</Text>
          <View style={styles.dates}>
            <DateTimeField
              mode="date"
              testID="admin-events-from"
              label={t('admin.events.periodFrom')}
              value={draftFrom}
              onChange={(value) => setPeriod(value, draftTo)}
            />
            <DateTimeField
              mode="date"
              testID="admin-events-to"
              label={t('admin.events.periodTo')}
              value={draftTo}
              onChange={(value) => setPeriod(draftFrom, value)}
              error={periodError ? t('admin.events.periodInvalid') : undefined}
            />
          </View>
          <View style={styles.presets}>
            {PRESETS.map((key) => (
              <TouchableOpacity
                key={key}
                accessibilityRole="button"
                accessibilityState={{ selected: preset === key }}
                aria-selected={preset === key}
                testID={`admin-events-preset-${key}`}
                style={[styles.preset, preset === key && styles.presetSelected]}
                onPress={() => {
                  const range = periodPreset(key)
                  setPeriod(range.from, range.to)
                }}
              >
                <Text type="bodySmallBold" color={preset === key ? theme.colors.white : theme.colors.secondary}>
                  {t(`admin.events.preset.${key}`)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <OptionChips<AdminEventStatus>
            testID="admin-events-status"
            label={t('admin.events.statusLabel')}
            options={ADMIN_EVENT_STATUSES.map((value) => ({ value, label: t(`admin.events.status.${value}`) }))}
            value={filters.status}
            onChange={(value) => commit({ status: value })}
          />
          <OptionChips<AdminEventPricing>
            testID="admin-events-pricing"
            label={t('admin.events.pricingLabel')}
            options={ADMIN_EVENT_PRICINGS.map((value) => ({ value, label: t(`admin.events.pricing.${value}`) }))}
            value={filters.pricing}
            onChange={(value) => commit({ pricing: value })}
          />
          <OptionChips<(typeof ADMIN_EVENT_CATEGORIES)[number]>
            testID="admin-events-category"
            label={t('admin.events.categoryLabel')}
            options={ADMIN_EVENT_CATEGORIES.map((value) => ({ value, label: t(`events.category.${value}`) }))}
            value={filters.category as (typeof ADMIN_EVENT_CATEGORIES)[number] | ''}
            onChange={(value) => commit({ category: value })}
          />
          <OptionChips<(typeof ADMIN_EVENT_FORMATS)[number]>
            testID="admin-events-format"
            label={t('admin.events.formatLabel')}
            options={ADMIN_EVENT_FORMATS.map((value) => ({ value, label: t(`events.format.${value}`) }))}
            value={filters.format as (typeof ADMIN_EVENT_FORMATS)[number] | ''}
            onChange={(value) => commit({ format: value })}
          />
          <OrganizerPicker
            testID="admin-events-organizer"
            value={filters.organizerId}
            onChange={(organizerId) => commit({ organizerId })}
          />
          <OptionChips<AdminEventSort>
            testID="admin-events-sort"
            label={t('admin.events.sortLabel')}
            options={ADMIN_EVENT_SORTS.map((value) => ({ value, label: t(`admin.events.sort.${value}`) }))}
            value={filters.sort}
            onChange={(value) => commit({ sort: value, order: '' })}
          />
          {filters.sort ? (
            <OptionChips<'asc' | 'desc'>
              testID="admin-events-order"
              label={t('admin.events.orderLabel')}
              options={[
                { value: 'asc', label: t('admin.events.order.asc') },
                { value: 'desc', label: t('admin.events.order.desc') },
              ]}
              value={filters.order || (filters.sort === 'date' ? 'asc' : 'desc')}
              onChange={(value) => commit({ order: value })}
            />
          ) : null}
        </View>
      ) : null}

      <View style={styles.countRow}>
        <Text type="bodySmallBold" color={theme.colors.secondary} testID="admin-events-total">
          {t('admin.events.total', { count: total })}
        </Text>
        {hasAnyAdminEventsFilter(filters) ? (
          <Button
            testID="admin-events-clear"
            title={t('admin.events.clear')}
            unfilled
            isSmall
            onPress={() => {
              setSearchText('')
              navigation.setParams(toAdminEventsParams(EMPTY_ADMIN_EVENTS_FILTERS))
            }}
          />
        ) : null}
      </View>

      {isLoading ? (
        <ActivityIndicator color={theme.colors.primary} />
      ) : isError ? (
        <Text type="bodySmall" color={theme.colors.error} testID="admin-events-error">
          {t('admin.events.error')}
        </Text>
      ) : events.length === 0 ? (
        <View style={styles.empty} testID="admin-events-empty">
          <Text type="subtitleBold" center>
            {t('admin.events.empty')}
          </Text>
        </View>
      ) : (
        <>
          <Text type="bodySmall" color={theme.colors.greyBlue} testID="admin-events-count">
            {t('admin.events.range', {
              from: formatCount(from, currentLanguage),
              to: formatCount(to, currentLanguage),
              total: formatCount(total, currentLanguage),
            })}
          </Text>
          {events.map((event) => (
            <AdminEventRow
              key={event.id}
              event={event}
              onPress={() => navigation.navigate('AdminEventDetail', { eventId: event.id })}
            />
          ))}
          {lastPage > 1 ? (
            <View style={styles.pager}>
              <Button
                testID="admin-events-prev"
                title={t('admin.events.previous')}
                unfilled
                isSmall
                disabled={filters.page <= 1 || isFetching}
                onPress={() => goToPage(Math.max(1, filters.page - 1))}
              />
              <Text type="bodySmall" color={theme.colors.greyBlue}>
                {t('admin.events.pageOf', { page: filters.page, pages: lastPage })}
              </Text>
              <Button
                testID="admin-events-next"
                title={t('admin.events.next')}
                unfilled
                isSmall
                disabled={filters.page >= lastPage || isFetching}
                onPress={() => goToPage(Math.min(lastPage, filters.page + 1))}
              />
            </View>
          ) : null}
        </>
      )}
    </AdminPage>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    searchRow: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.sm },
    searchBox: { flex: 1, minWidth: 0 },
    filterButton: {
      flexDirection: 'row',
      gap: 6,
      paddingHorizontal: theme.spacing.md,
      backgroundColor: theme.colors.primary,
      borderRadius: theme.border.jumbo,
      height: 36,
      justifyContent: 'center',
      alignItems: 'center',
    },
    panel: {
      gap: theme.spacing.md,
      padding: theme.spacing.lg,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
    },
    dates: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.md },
    presets: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.sm },
    preset: {
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs * 2,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.primary,
      backgroundColor: theme.colors.white,
    },
    presetSelected: { backgroundColor: theme.colors.primary },
    countRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing.sm,
    },
    empty: { alignItems: 'center', paddingVertical: theme.spacing.xxxl * 2 },
    pager: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing.sm,
    },
  })
